import { useEffect, useState } from 'react';
import { buildConfig } from '@aiptyg/config';
import { useLegalDocuments } from '../features/legal';
import { useMarketConfigs } from '../features/settings';
import * as legalService from '../features/legal/services/legal.service';
import { useToastStore } from '../store/toast.store';

function formatDateLabel(value: string | undefined): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function LegalDocumentsPage() {
  const config = buildConfig(import.meta.env);
  const addToast = useToastStore((s) => s.addToast);
  const markets = useMarketConfigs();
  const {
    items,
    selectedSlug,
    selectedMarketId,
    selectedDocument,
    loading,
    loadingList,
    loadingDetail,
    error,
    setSelectedSlug,
    selectMarket,
    reload,
  } = useLegalDocuments();
  const [draftTitle, setDraftTitle] = useState('');
  const [draftSummary, setDraftSummary] = useState('');
  const [draftEffectiveDate, setDraftEffectiveDate] = useState('');
  const [draftMarkdown, setDraftMarkdown] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selectedDocument) {
      setDraftTitle('');
      setDraftSummary('');
      setDraftEffectiveDate('');
      setDraftMarkdown('');
      return;
    }
    setDraftTitle(selectedDocument.title);
    setDraftSummary(selectedDocument.summary);
    setDraftEffectiveDate(selectedDocument.effective_date);
    setDraftMarkdown(selectedDocument.content_markdown);
  }, [selectedDocument]);

  const publicDetailUrl = selectedDocument
    ? `${config.apiBaseUrl}${selectedDocument.detail_path}?market_id=${encodeURIComponent(selectedMarketId)}`
    : '';
  const publicMarkdownUrl = selectedDocument
    ? `${config.apiBaseUrl}${selectedDocument.markdown_path}?market_id=${encodeURIComponent(selectedMarketId)}`
    : '';
  const canSave = Boolean(selectedDocument)
    && draftTitle.trim() !== ''
    && draftSummary.trim() !== ''
    && draftEffectiveDate.trim() !== ''
    && draftMarkdown.trim() !== ''
    && !saving;

  const handleSave = async () => {
    if (!selectedDocument || !canSave) return;
    setSaving(true);
    try {
      await legalService.updateLegalDocument(selectedDocument.slug, {
        marketId: selectedMarketId,
        title: draftTitle.trim(),
        summary: draftSummary.trim(),
        effectiveDate: draftEffectiveDate.trim(),
        contentMarkdown: draftMarkdown.trim(),
      });
      addToast('success', '法律文档已保存');
      await reload();
    } catch (e) {
      addToast('error', e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">法律文档</h1>
          <p className="mt-1 text-sm text-gray-500">
            统一查看当前线上生效的《服务条款》与《隐私协议》，并核对公开接口地址。
          </p>
        </div>
        <button
          onClick={() => {
            void reload();
          }}
          className="h-9 px-3 rounded-md border border-gray-300 text-sm hover:bg-gray-50 transition-colors"
        >
          刷新
        </button>
      </div>

      <label className="block max-w-xs space-y-1">
        <span className="text-sm font-medium text-gray-700">配置地区</span>
        <select
          value={selectedMarketId}
          onChange={(e) => selectMarket(e.target.value)}
          className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {markets.items.map((market) => (
            <option key={market.market_id} value={market.market_id}>
              {market.market_id} · {market.name}
            </option>
          ))}
        </select>
      </label>

      {(error || markets.error) && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error || markets.error}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[320px_minmax(0,1fr)] items-start">
        <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-200 px-5 py-4">
            <p className="text-sm font-medium text-gray-900">文档列表</p>
            <p className="mt-1 text-xs text-gray-500">共 {items.length} 份，公开接口均可匿名访问。</p>
          </div>

          {loadingList || markets.loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">暂无法律文档</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item) => {
                const active = item.slug === selectedSlug;
                return (
                  <button
                    key={item.slug}
                    onClick={() => setSelectedSlug(item.slug)}
                    className={`w-full text-left px-5 py-4 transition-colors ${
                      active ? 'bg-primary/5' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-sm font-medium ${active ? 'text-primary' : 'text-gray-900'}`}>
                          {item.title}
                        </p>
                        <p className="mt-2 text-xs leading-5 text-gray-500">{item.summary}</p>
                      </div>
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] font-medium text-gray-500">
                        {item.effective_date}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-200 px-5 py-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {selectedDocument?.title ?? '法律文档预览'}
                </p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>生效日期：{selectedDocument?.effective_date ?? '-'}</span>
                  <span>更新时间：{formatDateLabel(selectedDocument?.updated_at)}</span>
                </div>
              </div>
              {selectedDocument && (
                <div className="flex flex-wrap gap-2">
                  <a
                    href={publicDetailUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-9 items-center rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    打开 JSON 接口
                  </a>
                  <a
                    href={publicMarkdownUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-9 items-center rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    打开 Markdown 原文
                  </a>
                </div>
              )}
            </div>
          </div>

          {loadingDetail || loading || markets.loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="h-6 w-6 rounded-full border-2 border-gray-300 border-t-primary animate-spin" />
            </div>
          ) : !selectedDocument ? (
            <div className="py-24 text-center text-sm text-gray-400">选择一份文档查看详情</div>
          ) : (
            <div className="space-y-5 px-5 py-5">
              <div className="space-y-4 rounded-md border border-gray-200 bg-white px-4 py-4">
                <div className="grid gap-4 md:grid-cols-[1fr_160px]">
                  <label className="space-y-1">
                    <span className="text-sm font-medium text-gray-700">标题</span>
                    <input
                      value={draftTitle}
                      onChange={(e) => setDraftTitle(e.target.value)}
                      className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="text-sm font-medium text-gray-700">生效日期</span>
                    <input
                      value={draftEffectiveDate}
                      onChange={(e) => setDraftEffectiveDate(e.target.value)}
                      className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </label>
                </div>
                <label className="space-y-1 block">
                  <span className="text-sm font-medium text-gray-700">摘要</span>
                  <input
                    value={draftSummary}
                    onChange={(e) => setDraftSummary(e.target.value)}
                    className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </label>
                <label className="space-y-1 block">
                  <span className="text-sm font-medium text-gray-700">Markdown 内容</span>
                  <textarea
                    value={draftMarkdown}
                    onChange={(e) => setDraftMarkdown(e.target.value)}
                    rows={14}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs leading-5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </label>
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      void handleSave();
                    }}
                    disabled={!canSave}
                    className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-40 transition-colors"
                  >
                    {saving && <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                    保存文档
                  </button>
                </div>
              </div>

              <article
                className="legal-doc rounded-xl border border-gray-100 bg-gray-50 px-6 py-6"
                dangerouslySetInnerHTML={{ __html: selectedDocument.content_html }}
              />

              <details className="rounded-xl border border-gray-200 bg-white">
                <summary className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-gray-700">
                  查看 Markdown 源文
                </summary>
                <div className="border-t border-gray-200 p-4">
                  <pre className="max-h-[420px] overflow-auto rounded-lg bg-gray-950 p-4 text-xs leading-6 text-gray-100 whitespace-pre-wrap break-words">
                    {selectedDocument.content_markdown}
                  </pre>
                </div>
              </details>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
